/**
 * The sign-up screens' reads and calls (docs/signup-optimisation-work-orders.md):
 *
 *   - the assessment, its reads and the market set, read through the
 *     signed-in client (RLS lets members read all three, can_access_location),
 *     with the column names of migration 20261103120000 and nothing else;
 *   - POST /api/gbp-agent/signup-assess { action: market | picks | start }
 *     and POST /api/gbp-agent/signup-first-hour, with the session's token
 *     (flow A2: the market read fired when Your business opens, useMarketRead;
 *     the first hour released by the last step, releaseFirstHour, then again
 *     every 60 s while its post is not live, for up to an hour:
 *     useFirstHourNudge);
 *   - GET /api/gbp-agent/signup-proof for the start page;
 *   - the proved WhatsApp number given to the agent through agent-action-respond's
 *     settings op (notify_phones), in place of enable_locations' old whatsapp;
 *   - the polling hook the market, sizing, scorecard, plan and ask screens
 *     share: every 3 s while anything is still being read, every 15 s while
 *     only the first post is on its way, and not at all once everything
 *     landed;
 *   - the raw grid reads by keyword, for the leaderboard at a square
 *     (gridReads, picked out of the hook's reads).
 *
 * A read's payload is fetched once, when it is done, and only for the reads
 * a screen draws from: the twenty review crawls stay on the server except the
 * owner's own.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { FirstHourPayload, GridPayload, MarketBusiness, SignupAssessment, SignupOffer, SignupProof, SignupRead, ReadStatus, PlaceSuggestion } from './signupTypes';
import { SIGNUP_PROOF_PATH } from './startProof';

/* --------------------------------------------------------------- the db */

type DbError = { message: string } | null;
interface Select<T> extends PromiseLike<{ data: T[] | null; error: DbError }> {
  eq(column: string, value: unknown): Select<T>;
  in(column: string, values: unknown[]): Select<T>;
  order(column: string, opts: { ascending: boolean }): Select<T>;
  limit(n: number): Select<T>;
  maybeSingle(): PromiseLike<{ data: T | null; error: DbError }>;
}
/** The sign-up tables are not in the generated client types; the selects below name the migration's columns. */
interface UntypedDb {
  from(table: string): { select<T>(columns: string): Select<T> };
}
const db = supabase as unknown as UntypedDb;

const ASSESSMENT_COLUMNS = 'id, location_id, kind, status, keywords, pin, grid, summary, started_at, finished_at';
const READ_STATE_COLUMNS = 'read_key, status, error, finished_at';
const MARKET_COLUMNS =
  'place_id, cid, name, category, lat, lng, distance_km, photo_url, website, rating, reviews_count, photos_count, is_self, is_owner_pick, pack_rank, pack_keyword, pick_rank';

/** The latest assessment for the location, or null before the first one starts. */
export async function loadLatestAssessment(locationId: string): Promise<SignupAssessment | null> {
  const { data, error } = await db
    .from('gbp_agent_v1_signup_assessments')
    .select<SignupAssessment>(ASSESSMENT_COLUMNS)
    .eq('location_id', locationId)
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ?? null;
}

export async function loadReadStates(assessmentId: string): Promise<Array<Omit<SignupRead, 'payload'>>> {
  const { data, error } = await db.from('gbp_agent_v1_signup_reads').select<Omit<SignupRead, 'payload'>>(READ_STATE_COLUMNS).eq('assessment_id', assessmentId);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function loadReadPayloads(assessmentId: string, keys: string[]): Promise<Record<string, unknown>> {
  if (!keys.length) return {};
  const { data, error } = await db
    .from('gbp_agent_v1_signup_reads')
    .select<{ read_key: string; payload: unknown }>('read_key, payload')
    .eq('assessment_id', assessmentId)
    .in('read_key', keys);
  if (error) throw new Error(error.message);
  const out: Record<string, unknown> = {};
  for (const r of data ?? []) out[r.read_key] = r.payload;
  return out;
}

export async function loadMarket(locationId: string): Promise<MarketBusiness[]> {
  const { data, error } = await db.from('gbp_agent_v1_market_businesses').select<MarketBusiness>(MARKET_COLUMNS).eq('location_id', locationId);
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** The workspace the location bills in (gmb_locations.workspace_id), for the ask's checkout. */
export async function loadWorkspaceOf(locationId: string): Promise<string | null> {
  const { data, error } = await db.from('gmb_locations').select<{ workspace_id: string | null }>('workspace_id').eq('id', locationId).maybeSingle();
  if (error) throw new Error(error.message);
  return data?.workspace_id ?? null;
}

/* ------------------------------------------------------------- the routes */

export const ASSESS_PATH = '/api/gbp-agent/signup-assess';
export const FIRST_HOUR_PATH = '/api/gbp-agent/signup-first-hour';
export const SUGGEST_PATH = '/api/gbp-agent/signup-places';

async function authedPost(path: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    // A refusal the owner can act on carries a code (signup-assess 409: no_google_profile, no_map_pin); the step reads it.
    const err = new Error(String(json?.message ?? json?.error ?? `The request failed (${res.status}).`)) as Error & { code?: string };
    if (typeof json?.code === 'string') err.code = json.code;
    throw err;
  }
  return json ?? {};
}

export type AssessAction = 'market' | 'picks' | 'start';

export async function postAssess(action: AssessAction, locationId: string, extra: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
  console.log('[signup] assess', { action, location_id: locationId });
  return await authedPost(ASSESS_PATH, { action, location_id: locationId, ...extra });
}

/** Google Places suggestions for what the owner typed in the swap's search box (signup-places). */
export async function postSuggest(locationId: string, input: string): Promise<PlaceSuggestion[]> {
  const out = await authedPost(SUGGEST_PATH, { location_id: locationId, input });
  return Array.isArray(out.suggestions) ? (out.suggestions as PlaceSuggestion[]) : [];
}

/** B3's first hour (ten replies, one post): fired and forgotten, a failure is logged. */
export function postFirstHour(locationId: string): void {
  void authedPost(FIRST_HOUR_PATH, { location_id: locationId })
    .then(() => console.log('[signup] first hour started', { location_id: locationId }))
    .catch((e: Error) => console.warn('[signup] first hour did not start:', e.message));
}

/**
 * The sign-up's last step (Upgrade now or Stay on the trial) releases the
 * first hour (flow A2, D1 and C3): the route sets signup_released_at once and
 * then runs the first hour as postFirstHour does. Until this call the route
 * answers { status: 'held' } and nothing reaches Google. Fired by the last
 * step's two buttons; a failure is logged and never thrown, so the owner's
 * click always goes on to the checkout or the trial.
 */
export async function releaseFirstHour(locationId: string): Promise<void> {
  console.log('[signup] releasing the first hour', { location_id: locationId });
  try {
    const out = await authedPost(FIRST_HOUR_PATH, { location_id: locationId, release: true });
    console.log('[signup] first hour released', { location_id: locationId, status: out.status ?? null });
  } catch (e) {
    console.warn('[signup] the first hour was not released:', (e as Error).message);
  }
}

/** B3's route answers 202 and works in the background; each later call re-checks the post and the fixes. */
export const FIRST_HOUR_EVERY_MS = 60_000;
export const FIRST_HOUR_FOR_MS = 60 * 60_000;

/** The first post is live (the first_hour read's post.live_at). */
export function firstPostLive(reads: Record<string, SignupRead>): boolean {
  const fh = reads.first_hour;
  return fh?.status === 'done' && !!(fh.payload as FirstHourPayload | null)?.post?.live_at;
}

/**
 * While a sign-up screen is open and the first post is not live yet, the
 * first hour is called again every minute, for up to an hour from the first
 * call. The page turns it on only once the last step released the first hour
 * (flow A2: the first_hour read exists then), so nothing is asked before.
 */
export function useFirstHourNudge(locationId: string | null, active: boolean, live: boolean): void {
  const since = useRef<{ id: string; at: number } | null>(null);
  useEffect(() => {
    if (!active || !locationId || live) return;
    if (since.current?.id !== locationId) since.current = { id: locationId, at: Date.now() };
    const first = since.current.at;
    const t = window.setInterval(() => {
      if (Date.now() - first > FIRST_HOUR_FOR_MS) {
        window.clearInterval(t);
        return;
      }
      postFirstHour(locationId);
    }, FIRST_HOUR_EVERY_MS);
    return () => window.clearInterval(t);
  }, [active, locationId, live]);
}

export async function fetchProof(): Promise<SignupProof | null> {
  try {
    const res = await fetch(SIGNUP_PROOF_PATH);
    if (!res.ok) return null;
    return (await res.json()) as SignupProof;
  } catch {
    return null;
  }
}

/**
 * The proved number to the agent: agent-action-respond's settings op for the
 * location (settings.ts handleSettings, a signed-in caller names the
 * location), notify_phones read first and the number added to it, since the
 * op replaces the list. A read that fails writes nothing, so no number on the
 * list is ever dropped; the number stays proved on the profile either way.
 */
export async function giveAgentTheNumber(locationId: string, phone: string): Promise<boolean> {
  const read = await supabase.functions.invoke('agent-action-respond', { body: { location_id: locationId, op: 'settings' } });
  const now = (read.data as { settings?: { notify_phones?: unknown } } | null)?.settings?.notify_phones;
  if (read.error || !Array.isArray(now)) {
    console.warn('[signup] the agent settings could not be read, the number was not added', { location_id: locationId });
    return false;
  }
  const phones = [...new Set([...now.map(String), phone])];
  const { error } = await supabase.functions.invoke('agent-action-respond', { body: { location_id: locationId, op: 'settings', changes: { notify_phones: phones } } });
  if (error) {
    console.warn('[signup] the number did not reach the agent', { location_id: locationId });
    return false;
  }
  console.log('[signup] the agent has the WhatsApp number', { location_id: locationId, phones: phones.length });
  return true;
}

/** billing_status's offer for a v2 trial (B3); null when there is none or the call failed. */
export async function loadOffer(): Promise<SignupOffer | null> {
  const { data, error } = await supabase.functions.invoke('gbp-agent-portal', { body: { op: 'billing_status' } });
  if (error) return null;
  const offer = (data as { offer?: SignupOffer | null } | null)?.offer ?? null;
  if (!offer || typeof offer.code !== 'string' || typeof offer.ends_at !== 'string' || !(Number(offer.percent) > 0)) return null;
  // The months the offer covers ride along only as a number (signup-v2.ts currentOffer, Anand, 01 Nov 26).
  return { ...offer, cycles: typeof offer.cycles === 'number' && Number.isFinite(offer.cycles) ? offer.cycles : null };
}

/* --------------------------------------------------------- the polling hook */

/** The reads whose payloads a screen draws from. */
export function wantsPayload(key: string, selfPlaceId: string | null, fivePlaceIds: readonly string[] = []): boolean {
  if (['market', 'volumes', 'pack', 'posts_busy', 'first_hour', 'calendar'].includes(key)) return true;
  if (key.startsWith('grid:') || key.startsWith('audit:') || key.startsWith('website:')) return true;
  // The owner's reviews and the five's (the sizing step's "yours and theirs" ticks, Anand, 01 Nov 26); the rest of the market's stay on the server.
  if (!key.startsWith('reviews:')) return false;
  const pid = key.slice(8);
  return (!!selfPlaceId && pid === selfPlaceId) || fivePlaceIds.includes(pid);
}

/** Fast while reads run, slow while only the first post is on its way, off once everything landed. */
export function pollEvery(a: SignupAssessment | null, reads: Record<string, SignupRead>, marketEmpty: boolean): number | null {
  if (!a || marketEmpty) return 3000;
  if (a.status === 'running' || Object.values(reads).some((r) => r.status === 'running')) return 3000;
  const fh = reads.first_hour;
  if (!fh || fh.status === 'running') return 15000;
  const post = (fh.payload as FirstHourPayload | null)?.post;
  if (fh.status === 'done' && post && !post.live_at) return 15000;
  return null;
}

export interface SignupData {
  assessment: SignupAssessment | null;
  reads: Record<string, SignupRead>;
  /** null until the first read of the table; [] when the market is not read yet. */
  market: MarketBusiness[] | null;
  error: string | null;
  refresh: () => Promise<void>;
  setMarket: (rows: MarketBusiness[]) => void;
}

export function useSignupData(locationId: string | null, active: boolean): SignupData {
  const [assessment, setAssessment] = useState<SignupAssessment | null>(null);
  const [reads, setReads] = useState<Record<string, SignupRead>>({});
  const [market, setMarketState] = useState<MarketBusiness[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const readsRef = useRef<Record<string, SignupRead>>({});
  const marketRef = useRef<MarketBusiness[] | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    readsRef.current = {};
    marketRef.current = null;
    setAssessment(null);
    setReads({});
    setMarketState(null);
  }, [locationId]);

  const setMarket = useCallback((rows: MarketBusiness[]) => {
    marketRef.current = rows;
    setMarketState(rows);
  }, []);

  const refresh = useCallback(async () => {
    if (!locationId || busy.current) return;
    busy.current = true;
    try {
      const [a, m] = await Promise.all([
        loadLatestAssessment(locationId),
        marketRef.current?.some((b) => b.is_owner_pick) ? Promise.resolve(null) : loadMarket(locationId),
      ]);
      if (m) {
        marketRef.current = m;
        setMarketState(m);
      }
      setAssessment(a);
      if (a) {
        const states = await loadReadStates(a.id);
        const self = marketRef.current?.find((b) => b.is_self)?.place_id ?? a.pin?.place_id ?? null;
        const five = (marketRef.current ?? []).filter((b) => b.is_owner_pick && !b.is_self).map((b) => b.place_id);
        const prev = readsRef.current;
        const need = states
          .filter((s) => s.status === 'done' && wantsPayload(s.read_key, self, five))
          .filter((s) => !(prev[s.read_key] && prev[s.read_key].finished_at === s.finished_at && prev[s.read_key].payload != null))
          .map((s) => s.read_key);
        const payloads = await loadReadPayloads(a.id, need);
        const next: Record<string, SignupRead> = {};
        for (const s of states) {
          const kept = prev[s.read_key];
          const payload = s.read_key in payloads ? payloads[s.read_key] : kept && kept.finished_at === s.finished_at ? kept.payload : null;
          next[s.read_key] = { read_key: s.read_key, status: s.status as ReadStatus, error: s.error, finished_at: s.finished_at, payload };
        }
        readsRef.current = next;
        setReads(next);
      }
      setError(null);
    } catch (e) {
      console.warn('[signup] read failed:', (e as Error).message);
      setError((e as Error).message);
    } finally {
      busy.current = false;
      setTick((t) => t + 1);
    }
  }, [locationId]);

  useEffect(() => {
    if (!active || !locationId) return;
    void refresh();
  }, [active, locationId, refresh]);

  // The market is read again until the five are in it (the market rows may land before the picks), or its read ended.
  const marketFinal = !!reads.market && reads.market.status !== 'running';
  const wait = pollEvery(assessment, reads, !marketFinal && (!market || !market.some((b) => b.is_owner_pick)));
  useEffect(() => {
    if (!active || !locationId || wait == null) return;
    const t = window.setTimeout(() => void refresh(), wait);
    return () => window.clearTimeout(t);
  }, [active, locationId, wait, tick, refresh]);

  return { assessment, reads, market, error, refresh, setMarket };
}

/* ------------------------------------------------------- the market read */

/** A market read that failed: the route's sentence, and its code when the owner can act on it (no_google_profile, no_map_pin). */
export interface MarketReadError {
  message: string;
  code: string | null;
}

export interface MarketRead {
  readError: MarketReadError | null;
  /** Asks the market again (the step's Try again): clears the error, reads, refreshes. */
  retry: () => void;
  /** Resolves once the read asked in this page life has landed or failed (at once when none is on its way); never rejects. */
  settled: () => Promise<void>;
}

/**
 * The market read (POST signup-assess { action: 'market' }), fired when Your
 * business opens so it runs while the owner answers (flow A2, D2): the five
 * are on screen the moment Your market opens. It fires once per location in
 * this page life, while `active`, once the first read of the tables is in
 * (data.market is no longer null) and no assessment is running: none yet, or
 * only a finished or failed one (a testing account running the sign-up again
 * on a location it switched on before, Anand, 01 Nov 26: the start needs a
 * running assessment and answered 409 "Read the market first"). Your business
 * and Your market share this one hook (GrowthAgentStart), so moving between
 * them never asks twice. A failure keeps the route's sentence and code for
 * the market step; retry asks again; settled lets the switch-on wait for it.
 */
export function useMarketRead(locationId: string | null, active: boolean, data: SignupData): MarketRead {
  const asked = useRef<string | null>(null);
  const pending = useRef<Promise<void>>(Promise.resolve());
  const [readError, setReadError] = useState<MarketReadError | null>(null);
  const { refresh } = data;

  const read = useCallback(
    (id: string) => {
      asked.current = id;
      setReadError(null);
      console.log('[signup] market read asked', { location_id: id });
      pending.current = postAssess('market', id)
        .then(() => refresh())
        .catch((e: Error & { code?: string }) => {
          console.warn('[signup] market read failed:', e.message);
          setReadError({ message: e.message, code: e.code ?? null });
        });
    },
    [refresh],
  );

  // A new location starts with no error of its own.
  useEffect(() => {
    setReadError(null);
  }, [locationId]);

  const loaded = data.market !== null;
  const running = data.assessment?.status === 'running';
  useEffect(() => {
    if (!active || !locationId || !loaded || running || asked.current === locationId) return;
    read(locationId);
  }, [active, locationId, loaded, running, read]);

  const retry = useCallback(() => {
    if (locationId) read(locationId);
  }, [locationId, read]);

  const settled = useCallback(() => pending.current, []);

  return { readError, retry, settled };
}

/**
 * The raw grid reads by keyword (batch 2, decision D6): the payload of every
 * grid:<keyword> read that is done, from gbp_agent_v1_signup_reads through the
 * signed-in client, for the leaderboard at a square. The polling hook above
 * already loads these payloads once each read is done (wantsPayload keeps
 * every grid: key) and keeps them while finished_at stands, so they are
 * picked out of its reads here, once per change, and never fetched twice.
 */
export function gridReads(reads: Record<string, SignupRead>): Record<string, GridPayload> {
  const out: Record<string, GridPayload> = {};
  for (const [key, read] of Object.entries(reads)) {
    if (!key.startsWith('grid:') || read.status !== 'done') continue;
    const payload = read.payload as GridPayload | null;
    if (payload && Array.isArray(payload.squares)) out[key.slice('grid:'.length)] = payload;
  }
  return out;
}

export function useGridReads(reads: Record<string, SignupRead>): Record<string, GridPayload> {
  return useMemo(() => gridReads(reads), [reads]);
}

/* ------------------------------------------------------------ the layout */

/** The start flow's own breakpoint is 720 px (S6m note 5): under it the scorecard is cards, not a table. */
export function useIsPhone(): boolean {
  const query = '(max-width: 719px)';
  const read = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [phone, setPhone] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return phone;
}

/** Reduced motion turns the "With Fly" wave into one step. */
export function prefersStill(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
